import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSchoolData } from './SchoolDataProvider';
import { addDaysIso } from './calendarMath';
import AssignmentCard from './AssignmentCard';
import { NeedsADayCallout } from './NeedsADay';
import { BLOCK_LABEL, BLOCK_COLOR } from './blockTheme';

const BLOCK_ORDER = ['launch', 'math', 'la', 'logic', 'history', 'afternoon', 'fixed', 'calendar'];

export default function TodayPage() {
  const { byDate, today, courseFor, familyId, loading, holidayFor } = useSchoolData();
  const navigate = useNavigate();
  const [date, setDate] = useState(today); // browsable — defaults to today

  if (loading) return <p className="db-item-sub">Loading today…</p>;

  const dayItems = byDate.get(date) || [];
  const isToday = date === today;
  const holiday = holidayFor(date);

  const byBlock = {};
  for (const a of dayItems) (byBlock[a.block] ||= []).push(a);
  const blocksPresent = BLOCK_ORDER.filter(b => byBlock[b]?.length);

  const plannedMinutes = dayItems.reduce((s, a) => s + (a.estMinutes || 0), 0);
  const completedMinutes = dayItems.filter(a => a.status === 'complete').reduce((s, a) => s + (a.estMinutes || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
        <button className="btn btn--ghost btn--small" onClick={() => setDate(d => addDaysIso(d, -1))}>‹ Prev day</button>
        <span className="db-eyebrow" style={{ flex: 1, textAlign: 'center' }}>
          {date}{isToday && ' · Today'}
        </span>
        <button className="btn btn--ghost btn--small" onClick={() => setDate(d => addDaysIso(d, 1))}>Next day ›</button>
      </div>
      {!isToday && (
        <button className="btn btn--ghost btn--small" style={{ marginBottom: 8 }} onClick={() => setDate(today)}>
          Back to today
        </button>
      )}

      {/* Work with no day yet, pullable straight into today — so finishing
          Today can't look like finishing the week when tiles are still
          unplaced. Renders nothing unless we're actually looking at today. */}
      <NeedsADayCallout date={date} onSeeWeek={() => navigate('/school/week')} />

      {dayItems.length === 0 ? (
        <div className="db-card db-rest">
          <h2>{holiday || 'Nothing scheduled'}</h2>
          <p>{holiday ? 'No school today.' : isToday ? 'Enjoy the day off.' : 'Nothing landed on this day.'}</p>
        </div>
      ) : (
        <>
          <div className="db-meter">
            <div className="db-meter-track">
              <div className="db-meter-fill" style={{ width: `${plannedMinutes ? Math.round((completedMinutes / plannedMinutes) * 100) : 0}%` }} />
            </div>
            <span className="db-meter-label">{completedMinutes}m / {plannedMinutes}m</span>
          </div>

          {blocksPresent.map(block => {
            const items = byBlock[block].slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const doneCount = items.filter(i => i.status === 'complete').length;
            return (
              <section key={block} className={`db-card db-block ${doneCount === items.length ? 'db-block--done' : ''}`} style={{ '--bc': BLOCK_COLOR[block] }}>
                <div className="db-block-head">
                  <div>
                    <h2 className="db-block-name">{BLOCK_LABEL[block] || block}</h2>
                    <div className="db-block-meta">
                      <span>{doneCount}/{items.length} done</span>
                    </div>
                  </div>
                </div>
                <ul className="db-items">
                  {items.map(a => (
                    <AssignmentCard key={a.id} a={a} course={courseFor(a)} familyId={familyId} />
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
